import { mergeTablePaginationParams } from '@/utils'

import { customInstance } from '../auth/axios-config'

import { API_ENDPOINT_PROJECTS } from './constants'
import {
  CreateProjectRequest,
  Project,
  ProjectListParams,
  UpdateProjectRequest,
} from './types'

export interface ProjectListApiResponse {
  success: boolean
  data: Project[]
  pagination: {
    page: number
    page_size: number
    total_pages: number
    total_items: number
    has_next: boolean
    has_previous: boolean
  }
}

export interface ProjectListResponse {
  success: boolean
  data: Project[]
  metadata: {
    total: number
    limit: number
    offset: number
    returned: number
  }
  pagination?: {
    page: number
    page_size: number
    total_pages: number
    total_items: number
    has_next: boolean
    has_previous: boolean
  }
}

export interface ProjectDetailResponse {
  success: boolean
  data: Project[]
}

export interface CreateProjectResponse {
  success: boolean
  data: Project
  metadata: Record<string, unknown>
}

export interface UpdateProjectResponse {
  success: boolean
  data: Project
  metadata: Record<string, unknown>
}

export interface DeleteProjectResponse {
  success: boolean
  data: boolean
  metadata: Record<string, unknown>
}

export const projectApi = {
  getProjectList: async (
    params?: ProjectListParams
  ): Promise<ProjectListResponse> => {
    const url = `${API_ENDPOINT_PROJECTS}/list`

    const requestParams: Partial<import('@/schema').RequestFilters> = {
      q: params?.q,
      sort_by: params?.sort_by,
      limit: params?.limit,
      offset: params?.offset,
    }

    if (params?.filter_by) {
      requestParams.filter_by = params.filter_by as { [key: string]: unknown[] }
    }

    const requestBody = mergeTablePaginationParams(requestParams)

    const apiResponse = await customInstance<ProjectListApiResponse>({
      url,
      method: 'POST',
      data: {
        ...requestBody,
        search_type: params?.search_type,
        variant: params?.variant,
      },
    })

    const response: ProjectListResponse = {
      success: apiResponse.success,
      data: apiResponse.data,
      pagination: apiResponse.pagination,
      metadata: {
        total: apiResponse.pagination.total_items,
        limit: apiResponse.pagination.page_size,
        offset:
          (apiResponse.pagination.page - 1) * apiResponse.pagination.page_size,
        returned: apiResponse.data.length,
      },
    }

    return response
  },

  getProjectById: async (
    id: string | number
  ): Promise<ProjectDetailResponse> => {
    const url = `${API_ENDPOINT_PROJECTS}/${id}`

    const response = await customInstance<ProjectDetailResponse>({
      url,
      method: 'GET',
    })

    return response
  },

  createProject: async (
    data: CreateProjectRequest
  ): Promise<CreateProjectResponse> => {
    const url = `${API_ENDPOINT_PROJECTS}`

    const response = await customInstance<CreateProjectResponse>({
      url,
      method: 'POST',
      data,
    })

    return response
  },

  updateProject: async (
    id: string | number,
    data: UpdateProjectRequest
  ): Promise<UpdateProjectResponse> => {
    const url = `${API_ENDPOINT_PROJECTS}/${id}`

    const response = await customInstance<UpdateProjectResponse>({
      url,
      method: 'PUT',
      data,
    })

    return response
  },

  deleteProject: async (
    id: string | number
  ): Promise<DeleteProjectResponse> => {
    const url = `${API_ENDPOINT_PROJECTS}/${id}`

    const response = await customInstance<DeleteProjectResponse>({
      url,
      method: 'DELETE',
    })

    return response
  },
}
